import classNames from 'classnames';

import { INPUT_TYPES } from '@micro-apps/shared';

const formatISOToTime = time => time.replace(':00.000Z', '');

const INPUT_MODES = {
	// No virtual keyboard. For when the page implements its own keyboard input control.
	NONE: 'none',

	// Standard input keyboard for the user's current locale. (default value)
	TEXT: 'text',

	// Fractional numeric input keyboard containing the digits and decimal separator for the user's locale (typically . or ,). Devices may or may not show a minus key (-).
	DECIMAL: 'decimal',

	// Numeric input keyboard, but only requires the digits 0–9. Devices may or may not show a minus key.
	NUMERIC: 'numeric',

	// A telephone keypad input, including the digits 0–9, the asterisk (*), and the pound (#) key. Inputs that *require* a telephone number should typically use <input type="tel"> instead.
	TEL: 'tel',

	// A virtual keyboard optimized for search input. For instance, the return/submit key may be labeled "Search", along with possible other optimizations. Inputs that require a search query should typically use <input type="search"> instead.
	SEARCH: 'search',

	// A virtual keyboard optimized for entering email addresses. Typically includes the @character as well as other optimizations. Inputs that require email addresses should typically use <input type="email"> instead.
	EMAIL: 'email',

	// A keypad optimized for entering URLs. This may have the / key more prominent, for example. Enhanced features could include history access and so on. Inputs that require a URL should typically use <input type="url"> instead.
	URL: 'url',
};

const INPUT_MODE_MAP = {
	button: INPUT_MODES.TEXT,
	checkbox: INPUT_MODES.TEXT,
	color: INPUT_MODES.TEXT,
	date: INPUT_MODES.TEXT,
	'datetime-local': INPUT_MODES.TEXT,
	email: INPUT_MODES.EMAIL,
	file: INPUT_MODES.TEXT,
	hidden: INPUT_MODES.TEXT,
	image: INPUT_MODES.TEXT,
	month: INPUT_MODES.TEXT,
	number: INPUT_MODES.NUMERIC,
	password: INPUT_MODES.TEXT,
	radio: INPUT_MODES.TEXT,
	range: INPUT_MODES.TEXT,
	reset: INPUT_MODES.TEXT,
	search: INPUT_MODES.SEARCH,
	submit: INPUT_MODES.TEXT,
	tel: INPUT_MODES.TEL,
	text: INPUT_MODES.TEXT,
	time: INPUT_MODES.TEXT,
	url: INPUT_MODES.URL,
	week: INPUT_MODES.TEXT,
};

export default function useTextInput(
	{
		id,
		className,
		errorMessage = false,
		name,
		label,
		forceLabel = false,
		type = INPUT_TYPES.TEXT,
		isRequired = false,
		autoComplete = 'on',
		inputMode: inputModeOverride,
		placeholder,

		// uncontrolled
		defaultValue,

		// controlled
		value = '',
		onChange,

		allowBlank, // removed from ...props

		...props
	},
	ref
) {
	const hasError =
		(typeof errorMessage === 'boolean' && errorMessage === true) || errorMessage.length > 0;

	const placeholderToDisplay = isRequired ? (placeholder || label) + '*' : placeholder || label;

	const inputMode = inputModeOverride || INPUT_MODE_MAP[type.toLowerCase()];

	const inputProps = {
		id: id || `field--${name}`,
		name,
		required: isRequired,
		label: isRequired ? label + '*' : label,
		className: classNames('TextInput-field formElement-field ty-b2', className, {
			'formElement-field--filled': forceLabel || value,
			'formElement-field--invalid': hasError,
		}),
		ref: e => {
			if (ref) {
				ref.current = e;
			}
		},
		type: type.toLowerCase(),
		autoComplete,
		inputMode,
		defaultValue,
		value: type === INPUT_TYPES.TIME ? formatISOToTime(value) : value,
		onChange,
		placeholder: placeholderToDisplay,
		...props,
	};

	const fieldClassNames = classNames('TextInput', className, {
		'TextInput--error': hasError,
	});

	return {
		inputProps,
		errorMessage,
		fieldClassNames,
	};
}
